/**
 * <nav-drawer> — the two-level mobile and tablet navigation. docs/16.
 *
 * ---------------------------------------------------------------------------
 * THE ELEMENT NAME IS `nav-drawer`, NOT `menu-drawer`.
 *
 * docs/47 §5 line 4.13 asks for `<menu-drawer>`. That name is taken: Dawn
 * defines MenuDrawer in global.js and snippets/facets.liquid uses it for the
 * mobile filter drawer, which Step 8.4 keeps. Re-registering the tag throws
 * NotSupportedError and would take the filters down with it.
 *
 * `header-drawer` is free — Dawn's HeaderDrawer subclass existed only for
 * snippets/header-drawer.liquid, which header-menu-mobile.liquid replaces —
 * but reusing it would mean our drawer inherits Dawn's behaviour by name
 * while sharing none of its implementation. A distinct name is clearer.
 * ---------------------------------------------------------------------------
 *
 * THE FOCUS TRAP IS OWNED BY THIS ELEMENT.
 *
 * Dawn's global trapFocus() is leaky by one stop — measured at Gate 2 and
 * recorded in docs/51 §8.1. It attaches its keydown handler from a `focusin`
 * listener that fires for only three elements, while a `focusout` listener
 * removes it on every focus move; tabbing off the last focusable lands on
 * <body> and only returns on the NEXT Tab.
 *
 * docs/51 §8 claimed the drawers were a separate implementation and might be
 * fine. They are a separate implementation, and they call the same leaky
 * trapFocus. So the trap lives here, on the element, in the same shape
 * ModalDialog uses — nothing can detach it, and `hidden` submenus are
 * excluded from the ring so Tab cannot land on an off-screen panel.
 */

class NavDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.nav-drawer__panel');
    /* THE OPENER IS FOUND BY WHAT IT CONTROLS, NOT BY WHERE IT LIVES.
       This read `.header__toggle[aria-controls=…]` until 2026-08-11, which
       tied the element to one call site — the header's hamburger. The app
       bar's Menu button opens a second drawer with the same behaviour and is
       not a header toggle, and the alternatives were both worse: give that
       button a `header__toggle` class it should not have and inherit the
       header's styling with it, or write a second focus trap. docs/51 §8.1
       measured Dawn's trap leaking by one stop and this element exists to
       own the correct one — a second implementation is the thing to avoid.
       `[aria-controls]` still matches the header toggle, so nothing moved. */
    this.toggle = document.querySelector('[aria-controls="' + this.id + '"]');
    this.openedBy = null;
    this.activeSubmenu = null;

    this.toggle?.addEventListener('click', () => {
      if (this.hasAttribute('open')) this.close();
      else this.open(this.toggle);
    });

    this.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.querySelectorAll('.nav-menu__toggle').forEach((button) => {
      button.addEventListener('click', () => this.openSubmenu(button));
    });

    this.querySelectorAll('[data-drawer-back]').forEach((button) => {
      button.addEventListener('click', () => this.closeSubmenu());
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (this.activeSubmenu) this.closeSubmenu();
        else this.close();
        return;
      }
      if (event.key === 'Tab') this.wrapTab(event);
    });

    // The drawer only exists below 1024. Crossing up into the desktop band
    // with it open would otherwise leave the page inert and scroll-locked
    // behind a drawer nobody can see.
    this.desktop = window.matchMedia('(min-width: 1024px)');
    this.desktop.addEventListener('change', (event) => {
      if (event.matches && this.hasAttribute('open')) this.close();
    });

    this.bindSheetDrag();
  }

  /**
   * DRAG TO DISMISS — the app bar's bottom sheet only. 2026-10-27.
   *
   * The sheet drew an iOS grab handle that did nothing; on a phone, pulling a
   * bottom sheet down is the first thing a thumb tries. A downward drag that
   * starts while the sheet's own content is scrolled to the top now moves the
   * panel with the finger, and on release it closes if it travelled past 30%
   * of its height OR was flicked (over 0.11px/ms, the threshold iOS sheets
   * use), and otherwise springs back on the open transition.
   *
   * Touch events, not pointer events: the panel scrolls, and a pointer stream
   * is cancelled the moment the browser claims the gesture for scrolling.
   * touchmove is non-passive only so it can preventDefault WHILE dragging;
   * an upward start, or a start mid-scroll, is left entirely to the browser.
   */
  bindSheetDrag() {
    if (!this.classList.contains('app-sheet') || !this.panel) return;
    const panel = this.panel;
    let startY = 0;
    let startTime = 0;
    let travel = 0;
    let tracking = false;
    let dragging = false;

    panel.addEventListener(
      'touchstart',
      (event) => {
        if (!this.hasAttribute('open') || event.touches.length !== 1) return;
        tracking = panel.scrollTop <= 0;
        dragging = false;
        travel = 0;
        startY = event.touches[0].clientY;
        startTime = performance.now();
      },
      { passive: true }
    );

    panel.addEventListener(
      'touchmove',
      (event) => {
        if (!tracking) return;
        const delta = event.touches[0].clientY - startY;
        if (!dragging) {
          if (delta < -4) tracking = false; // heading up: that is a scroll
          if (delta <= 4) return;
          dragging = true;
          panel.style.transition = 'none';
        }
        event.preventDefault();
        travel = Math.max(0, delta);
        panel.style.transform = 'translateY(' + travel + 'px)';
      },
      { passive: false }
    );

    const release = () => {
      tracking = false;
      if (!dragging) return;
      dragging = false;
      const velocity = travel / Math.max(1, performance.now() - startTime);
      // Clearing both in one frame lets the CSS transition run FROM where the
      // finger left the panel, to closed or back to open.
      panel.style.transition = '';
      panel.style.transform = '';
      if (travel > panel.offsetHeight * 0.3 || (travel > 24 && velocity > 0.11)) this.close();
    };
    panel.addEventListener('touchend', release);
    panel.addEventListener('touchcancel', release);
  }

  open(opener) {
    this.openedBy = opener || null;
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    this.setBackgroundInert(true);

    // Focus the first thing in the panel rather than the panel itself, so a
    // screen reader starts on the first menu item instead of announcing an
    // empty container. docs/43 §4.1.
    const first = this.focusable()[0];
    (first || this.panel).focus();
  }

  close() {
    this.closeSubmenu();
    this.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    this.setBackgroundInert(false);
    this.openedBy?.focus();
    this.openedBy = null;
  }

  openSubmenu(button) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    this.activeSubmenu = { panel, button };
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('nav-submenu--open'));
    button.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-drawer-back]')?.focus();
  }

  closeSubmenu() {
    if (!this.activeSubmenu) return;
    const { panel, button } = this.activeSubmenu;
    panel.classList.remove('nav-submenu--open');
    button.setAttribute('aria-expanded', 'false');
    this.activeSubmenu = null;

    const done = () => {
      if (!panel.classList.contains('nav-submenu--open')) panel.hidden = true;
    };
    panel.addEventListener('transitionend', done, { once: true });
    window.setTimeout(done, 400);

    button.focus();
  }

  /**
   * Visible focusables only. A closed level-2 panel is `hidden`, so its links
   * are already out of the tab order; an OPEN one covers level 1 completely
   * (docs/16 §7), so level 1's links are visually gone but still focusable.
   * Restricting the ring to the open panel is what stops Tab walking through
   * a list the user cannot see.
   */
  focusable() {
    /* FALLS BACK TO THE PANEL, and that fallback is what makes this element
       reusable. The scope was `.nav-drawer__content` only until 2026-08-11;
       the app sheet's wrapper is `.app-sheet__content`, so this returned an
       empty array, `open()` focused nothing, the Escape handler on this
       element never saw a keydown because focus was still on <body>, and
       three separate checks failed pointing at three different features.

       docs/54 §1.-24: "A SELECTOR THAT MATCHES IS NOT A SELECTOR THAT IS
       RIGHT" — here it matched nothing at all, which is the same failure
       from the other end and just as silent.

       The panel rather than `this`, because the backdrop is a sibling of the
       panel and holds nothing focusable; and it also catches the sheet's
       close button, which sits OUTSIDE the content wrapper and would
       otherwise be missing from the ring that dismisses the thing it
       dismisses. */
    const scope = this.activeSubmenu
      ? this.activeSubmenu.panel
      : this.querySelector('.nav-drawer__content') || this.panel;
    if (!scope) return [];
    return Array.from(
      scope.querySelectorAll(
        'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]), select, textarea',
      ),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  wrapTab(event) {
    if (!this.hasAttribute('open')) return;
    const elements = this.focusable();
    if (elements.length === 0) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement;

    if (!event.shiftKey && (active === last || !this.contains(active))) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (active === first || !this.contains(active))) {
      event.preventDefault();
      last.focus();
    }
  }

  /**
   * Same contract as ModalDialog.setBackgroundInert — focus trapping stops
   * Tab but not a screen reader's virtual cursor. docs/16 §11 lists "page
   * behind not aria-hidden" as one of the reference's gaps.
   *
   * The header itself stays live: docs/16 §3.1 makes the header row visible
   * and interactive above an open drawer on purpose, and the hamburger is
   * inside it, so marking it inert would make the drawer impossible to close
   * with the control that opened it.
   */
  setBackgroundInert(on) {
    /* SKIP WHATEVER BODY-LEVEL ANCESTOR CONTAINS THIS DRAWER, NOT
       SPECIFICALLY ITS `.shopify-section`.

       This read `this.closest('.shopify-section')` until 2026-08-11, which is
       true of the header drawer because the header IS a section. The app
       sheet is rendered from `theme.liquid` as a direct child of <body>, so
       `closest()` returned null, nothing matched the skip, and the loop set
       `inert` on EVERY body child — including the sheet itself. An inert
       element cannot take focus, so `open()` focused nothing, the Escape
       handler never received a keydown, and the drawer could not be closed
       by any means. Three checks failed and none of them named the cause.

       `el.contains(this)` is true for the header's section AND for the sheet
       itself (an element contains itself), so one test covers both call
       sites and any future one. Assuming your own call site is exactly what
       docs/54 §5.6 records Dawn doing — "Dawn throws on markup it assumes
       but does not own" — arriving from inside our own code. */
    for (const el of document.body.children) {
      if (el.contains(this) || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
      if (on) {
        if (el.hasAttribute('aria-hidden')) el.dataset.hadAriaHidden = 'true';
        el.setAttribute('aria-hidden', 'true');
        el.inert = true;
      } else if (!el.dataset.hadAriaHidden) {
        el.removeAttribute('aria-hidden');
        el.inert = false;
      } else {
        delete el.dataset.hadAriaHidden;
        el.inert = false;
      }
    }
  }
}

if (!customElements.get('nav-drawer')) {
  customElements.define('nav-drawer', NavDrawer);
}
